import { useQueries, useQuery, useMutation } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { ITalkParticipant } from '../../Interfaces/ITalkParticipant';


export interface ITalkParticipantsResponse {
  participants: ITalkParticipant[];
}

/**
 * Lädt die Teilnehmer eines Talk-Raums (über Backend -> Nextcloud).
 * GET /legaltransaction/talkrooms/{idLegalTransactionTalkRoom}/participants
 */
export const useGetTalkRoomParticipants = (
  idLegalTransactionTalkRoom: number | null,
  enabled: boolean = true
) => {
  const {
    data,
    isLoading: isLoadingParticipants,
    isFetching: isFetchingParticipants,
    error,
    refetch,
  } = useQuery<ITalkParticipantsResponse>({
    queryKey: ['talkroom', idLegalTransactionTalkRoom, 'participants'],
    enabled:
      enabled && !!idLegalTransactionTalkRoom && idLegalTransactionTalkRoom > 0,
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/talkrooms/${idLegalTransactionTalkRoom}/participants`,
      }),
  });

  return {
    participants: data?.participants ?? [],
    isLoadingParticipants,
    isFetchingParticipants,
    error,
    refetchParticipants: refetch,
  };
};

/**
 * Lädt Teilnehmer-Counts für mehrere Talk-Räume parallel.
 * Für neue Draft-Räume (id < 1) wird keine Query erzeugt.
 */
export const useGetTalkRoomParticipantCounts = (
  talkRoomIds: number[],
  enabled: boolean = true
) => {
  const ids = (talkRoomIds ?? []).filter(id => id > 0);

  const results = useQueries({
    queries: ids.map(id => ({
      queryKey: ['talkroom', id, 'participants'],
      enabled: enabled && id > 0,
      queryFn: () =>
        customFetcher({
          endpoint: `/legaltransaction/talkrooms/${id}/participants`,
        }) as Promise<ITalkParticipantsResponse>,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  // Map id -> count (ohne downlevelIteration-Probleme)
  const countById: Record<number, number> = {};
  const participantsById: Record<number, ITalkParticipant[]> = {};
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    const q = results[i];
    participantsById[id] = q?.data?.participants ?? [];
    countById[id] = participantsById[id].length;
  }

  const isLoadingAny = results.some(r => r.isLoading);
  const isFetchingAny = results.some(r => r.isFetching);

  return {
    countById,
    participantsById,
    results,
    isLoadingAny,
    isFetchingAny,
  };
};

/**
 * OPTIONAL: Participant-Endpoint nachladen/refresh erzwingen.
 * (technisch identisch zu refetch, aber manchmal praktisch als Mutation-Trigger)
 */
export const useRefreshTalkRoomParticipants = () => {
  const { mutateAsync: refreshParticipants, isPending: isPendingRefresh } =
    useMutation({
      mutationFn: (idLegalTransactionTalkRoom: number) =>
        customFetcher({
          endpoint: `/legaltransaction/talkrooms/${idLegalTransactionTalkRoom}/participants`,
          method: 'GET',
        }) as Promise<ITalkParticipantsResponse>,
    });

  return { refreshParticipants, isPendingRefresh };
};
